import React, { useState, useEffect } from 'react'
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
  IconButton,
  Grid,
  InputAdornment,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
  ListItemText,
  Menu as MuiMenu
} from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
  CREATE_ADVANCED_MODIFIER_GROUP,
  UPDATE_ADVANCED_MODIFIER_GROUP,
  DELETE_ADVANCED_MODIFIER_GROUP,
  CREATE_ADVANCED_MODIFIER
} from '../../apollo/advancedMenuMutations'
import { GET_ADVANCED_MODIFIER_GROUPS, GET_ADVANCED_MODIFIERS, GET_RESTAURANT_ITEMS, GET_ADVANCED_OPTION_GROUPS } from '../../apollo/advancedMenuQueries'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import SwapVertIcon from '@mui/icons-material/SwapVertRounded'
import SouthIcon from '@mui/icons-material/South'
import { Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from '@mui/material'
import { mw, chipSx, searchFieldSx, sectionLabelSx, optionCardSx, pillBtnSx, pillBtnPrimarySx, iconBtnSx, iconBtnDangerSx, dialogPaperSx } from '../../views/menuWorkspaceStyles'

// Small bold field-group label ("Add Modifier", "Selected").
const fieldLabelSx = { color: mw.color.textSecondary, fontSize: 13, fontWeight: 700 }

const Column3GroupBuilder = ({ onGroupCreated, refreshModifiers, initialGroup, onDirtyChange }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [minSelections, setMinSelections] = useState(0)
  const [maxSelections, setMaxSelections] = useState(5)
  const [isRequired, setIsRequired] = useState(false)
  // Whether the GROUP itself is optional (skippable / opt-in) or required.
  // Separate from isRequired, which governs the modifiers inside the group.
  const [groupRequirement, setGroupRequirement] = useState('REQUIRED')
  // Flat extra cost charged once when the customer picks >=1 modifier from this
  // group (e.g. "make it a Plate +$3"). Optional; 0 = free group.
  const [additionalCost, setAdditionalCost] = useState('')
  const [isPizzaMode, setIsPizzaMode] = useState(false)
  const [allowDuplicates, setAllowDuplicates] = useState(false)
  const [maxDuplicates, setMaxDuplicates] = useState(4)
  const [duplicatePricingMode, setDuplicatePricingMode] = useState('SAME')
  // INCREMENTAL tier prices: { "1": price, "2": price, ... } — the price of the
  // Nth unit. The engine sums these, so picking 3 costs prices[1]+prices[2]+prices[3].
  const [duplicatePrices, setDuplicatePrices] = useState({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Modifier selection and pricing
  const [selectedModifiers, setSelectedModifiers] = useState([])
  const [modifierSearch, setModifierSearch] = useState('')
  // Which catalog the "Add" picker shows: plain MODIFIERs or full menu ITEMs
  // (item-as-modifier). Item members reveal their own modifier groups to the
  // customer when picked (nested / full customize).
  const [pickerMode, setPickerMode] = useState('MODIFIER')
  // Drag-and-drop member ordering: reorder in a small dialog, then Update/
  // Create Group persists displayOrder = list position (what customers see).
  const [membersReorderOpen, setMembersReorderOpen] = useState(false)
  const [memberOrderList, setMemberOrderList] = useState([])
  const openMembersReorder = () => {
    setMemberOrderList(selectedModifiers.map(m => ({ key: m.key, name: m.modifierName })))
    setMembersReorderOpen(true)
  }
  const handleMembersDragEnd = (result) => {
    if (!result.destination) return
    setMemberOrderList(prev => {
      const next = prev.slice()
      const [moved] = next.splice(result.source.index, 1)
      next.splice(result.destination.index, 0, moved)
      return next
    })
  }
  const applyMembersOrder = () => {
    const pos = new Map(memberOrderList.map((m, idx) => [m.key, idx]))
    setSelectedModifiers(prev => prev.slice().sort((a, b) => (pos.get(a.key) ?? 0) - (pos.get(b.key) ?? 0)))
    setMembersReorderOpen(false)
  }
  // Keys of item-as-modifier members whose nested groups are expanded for preview.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())
  const toggleExpanded = (key) => setExpandedKeys((prev) => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  // Pizza mode variant columns
  const [variantColumns, setVariantColumns] = useState(['Small', 'Medium', 'Large'])
  const [newVariantName, setNewVariantName] = useState('')

  // Edit mode
  const [editingGroup, setEditingGroup] = useState(null)

  const restaurantId = localStorage.getItem('restaurantId')

  // Query existing groups
  const { data: groupsData, loading: groupsLoading } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // Query modifiers from Column 2
  const { data: modifiersData, loading: modifiersLoading } = useQuery(GET_ADVANCED_MODIFIERS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // Option groups (free choice lists like spice level) can be attached to a
  // modifier as a follow-up question.
  const { data: optionGroupsData } = useQuery(GET_ADVANCED_OPTION_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  // Query menu items so they can be added as members (item-as-modifier).
  const { data: itemsData, loading: itemsLoading } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const refetchQueries = [{ query: GET_ADVANCED_MODIFIER_GROUPS, variables: { restaurantId } }]

  const [createGroup, { loading: createLoading }] = useMutation(CREATE_ADVANCED_MODIFIER_GROUP, {
    refetchQueries,
    onCompleted: () => {
      // Save and close: hand control back to the parent (closes the dialog,
      // refreshes the list, shows the toast). Fallback message if standalone.
      setDirty(false)
      if (onGroupCreated) {
        onGroupCreated(t('Saved! Modifier group created.'))
      } else {
        setSuccess(t('Saved! Modifier group created.'))
        setTimeout(() => setSuccess(''), 3000)
      }
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const [updateGroup, { loading: updateLoading }] = useMutation(UPDATE_ADVANCED_MODIFIER_GROUP, {
    refetchQueries,
    onCompleted: () => {
      setDirty(false)
      if (onGroupCreated) {
        onGroupCreated(t('Saved! Modifier group updated.'))
      } else {
        setSuccess(t('Saved! Modifier group updated.'))
        setTimeout(() => setSuccess(''), 3000)
      }
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const [deleteGroup] = useMutation(DELETE_ADVANCED_MODIFIER_GROUP, {
    refetchQueries,
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  // Inline-create a brand-new modifier from the search box and immediately add
  // it to this group — so the user need not leave to Column 1 for a missing one.
  const [createModifier, { loading: creatingModifier }] = useMutation(CREATE_ADVANCED_MODIFIER, {
    // Refresh the library so the new modifier shows in the picker / "Used In".
    refetchQueries: [{ query: GET_ADVANCED_MODIFIERS, variables: { restaurantId } }],
    awaitRefetchQueries: true,
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const handleCreateAndAddModifier = async () => {
    const name = modifierSearch.trim()
    if (!name) return
    const { data: created } = await createModifier({
      variables: { restaurantId, input: { name, modifierType: 'OTHER', isModifier: true, isIngredient: false } }
    })
    const mod = created?.createAdvancedModifier
    if (!mod) return
    handleAddMember(mod, 'MODIFIER')
    setModifierSearch('')
  }

  const loading = createLoading || updateLoading

  // Unsaved-changes tracking. We set `dirty` whenever the user edits an editable
  // field, and clear it on (re)load and on a successful save. A ref suppresses
  // the dirty flag while we programmatically populate the form from a group.
  const [dirty, setDirty] = useState(false)
  const suppressDirtyRef = React.useRef(false)

  // Report dirty state up so the parent can guard the Close/backdrop actions.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  // Any change to an editable value marks the form dirty — unless we're mid-load.
  useEffect(() => {
    if (suppressDirtyRef.current) return
    setDirty(true)
  }, [
    name, description, minSelections, maxSelections, isRequired, groupRequirement,
    additionalCost, isPizzaMode, allowDuplicates, maxDuplicates, duplicatePricingMode,
    duplicatePrices, selectedModifiers, variantColumns
  ])

  // Set editingGroup from parent prop
  useEffect(() => {
    if (initialGroup) {
      setEditingGroup(initialGroup)
    }
  }, [initialGroup])

  // Populate form when editingGroup changes
  useEffect(() => {
    // Suppress dirty-tracking while we programmatically fill the form.
    suppressDirtyRef.current = true
    if (editingGroup) {
      setName(editingGroup.name || '')
      setDescription(editingGroup.description || '')
      setMinSelections(editingGroup.minSelections ?? 0)
      setMaxSelections(editingGroup.maxSelections ?? 5)
      setIsRequired(editingGroup.isRequired || false)
      setGroupRequirement(editingGroup.groupRequirement || 'REQUIRED')
      setAdditionalCost(
        editingGroup.additionalCost ? String(editingGroup.additionalCost) : ''
      )
      setAllowDuplicates(editingGroup.allowDuplicates || false)
      setMaxDuplicates(editingGroup.maxDuplicates || 4)
      setDuplicatePricingMode(editingGroup.duplicatePricingMode || 'SAME')
      // Load saved incremental tier rules. Normalize to { mode, extra }:
      // new format is already objects; legacy numbers become PLUS with the
      // number treated as an extra over base (best-effort migration).
      const dpRaw = editingGroup.duplicatePrices
      const dpParsed = dpRaw ? (typeof dpRaw === 'string' ? JSON.parse(dpRaw) : dpRaw) : {}
      const normalized = {}
      Object.entries(dpParsed).forEach(([k, v]) => {
        if (v && typeof v === 'object') {
          normalized[k] = { mode: v.mode || 'SAME', extra: v.extra ?? 0 }
        } else {
          // Legacy absolute number → keep as a PLUS extra so it stays editable.
          normalized[k] = { mode: 'PLUS', extra: parseFloat(v) || 0 }
        }
      })
      setDuplicatePrices(normalized)

      // Determine pizza mode from modifiers
      const hasPizzaMode = editingGroup.modifiers?.some(m => m.priceOverrideMode === 'VARIANT_BASED')
      setIsPizzaMode(hasPizzaMode || false)

      // Derive variant columns from first modifier with variantPrices
      if (hasPizzaMode) {
        const firstWithVariants = editingGroup.modifiers?.find(m => m.variantPrices && Object.keys(m.variantPrices).length > 0)
        if (firstWithVariants) {
          setVariantColumns(Object.keys(firstWithVariants.variantPrices))
        }
      } else {
        setVariantColumns(['Small', 'Medium', 'Large'])
      }

      // Map nested members (plain modifiers and item-as-modifier) to flat format.
      // Each carries a stable `key` so the pricing table can target it regardless
      // of whether it's a modifier or an item.
      setSelectedModifiers((editingGroup.modifiers || []).map(m => {
        const isItem = m.memberType === 'ITEM' || (!!m.item && !m.modifier)
        const id = isItem ? (m.item?._id || m.itemId) : (m.modifier?._id || m.modifierId)
        return {
          key: isItem ? `item_${id}` : `mod_${id}`,
          memberType: isItem ? 'ITEM' : 'MODIFIER',
          modifierId: isItem ? null : id,
          itemId: isItem ? id : null,
          modifierName: isItem
            ? (m.item?.name || m.modifierName || '')
            : (m.modifier?.name || m.modifierName || ''),
          priceOverrideMode: m.priceOverrideMode || 'FIXED',
          fixedPrice: m.fixedPrice || 0,
          variantPrices: m.variantPrices || {},
          displayOrder: m.displayOrder || 0,
          isDefault: m.isDefault || false,
          // For item members: the item's own groups, shown in the expandable preview.
          itemModifierGroups: isItem ? (m.item?.modifierGroups || []) : [],
          // For modifier members: groups revealed when this modifier is picked.
          // Only the top level matters here — each nested group is edited as a
          // group in its own right. An option group comes back with a synthetic
          // "og_<id>" _id, so keep its real id for saving.
          nestedGroups: (m.nestedGroups || []).map(g => (
            g.isOptionGroup
              ? { _id: String(g._id), name: g.name, isOptionGroup: true, optionGroupId: String(g.optionGroupId) }
              : { _id: String(g._id), name: g.name }
          ))
        }
      }))

      setModifierSearch('')
      setNewVariantName('')
    }
    // After this batch of programmatic setState flushes (and the dirty-watcher
    // effect runs with suppression on), re-enable tracking and reset to clean.
    const id = setTimeout(() => {
      suppressDirtyRef.current = false
      setDirty(false)
    }, 0)
    return () => clearTimeout(id)
  }, [editingGroup])

  const resetForm = () => {
    setEditingGroup(null)
    setName('')
    setDescription('')
    setMinSelections(0)
    setMaxSelections(5)
    setIsRequired(false)
    setGroupRequirement('REQUIRED')
    setAdditionalCost('')
    setIsPizzaMode(false)
    setAllowDuplicates(false)
    setMaxDuplicates(4)
    setDuplicatePricingMode('SAME')
    setSelectedModifiers([])
    setModifierSearch('')
    setVariantColumns(['Small', 'Medium', 'Large'])
    setNewVariantName('')
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setError('')
    setSuccess('')
  }

  const handleDeleteGroup = (groupId) => {
    if (window.confirm(t('Are you sure you want to delete this group?'))) {
      // If we're editing this group, reset form
      if (editingGroup && editingGroup._id === groupId) {
        resetForm()
      }
      deleteGroup({ variables: { id: groupId } })
    }
  }

  // Add a member to the group: either a plain modifier or a menu item used as an
  // add-on (memberType ITEM). Both share the inline pricing table below, keyed by
  // a stable `key` ('mod_<id>' / 'item_<id>').
  const handleAddMember = (entity, memberType) => {
    const key = memberType === 'ITEM' ? `item_${entity._id}` : `mod_${entity._id}`
    if (selectedModifiers.find(m => m.key === key)) {
      return
    }

    const defaultVariantPrices = {}
    variantColumns.forEach(col => {
      defaultVariantPrices[col] = 0
    })

    const newMember = {
      key,
      memberType,
      modifierId: memberType === 'ITEM' ? null : entity._id,
      itemId: memberType === 'ITEM' ? entity._id : null,
      modifierName: entity.name,
      priceOverrideMode: isPizzaMode ? 'VARIANT_BASED' : 'FIXED',
      fixedPrice: 0,
      variantPrices: defaultVariantPrices,
      displayOrder: selectedModifiers.length,
      isDefault: false,
      // For item members: the item's own groups, shown in the expandable preview.
      itemModifierGroups: memberType === 'ITEM' ? (entity.modifierGroups || []) : [],
      nestedGroups: []
    }

    setSelectedModifiers([...selectedModifiers, newMember])
  }

  const handleRemoveMember = (key) => {
    setSelectedModifiers(selectedModifiers.filter(m => m.key !== key))
  }

  // --- Nested groups: a modifier in this group can reveal groups of its own ---
  // (e.g. picking "Pearls" then asking for a sweetness level). The attachment is
  // per placement, so the same modifier stays a plain toggle in other groups.
  const [nestedAnchor, setNestedAnchor] = useState(null)
  const [nestedTargetKey, setNestedTargetKey] = useState(null)

  const openNestedMenu = (e, key) => {
    e.stopPropagation()
    setNestedTargetKey(key)
    setNestedAnchor(e.currentTarget)
  }
  const closeNestedMenu = () => {
    setNestedAnchor(null)
    setNestedTargetKey(null)
  }
  // isOptionGroup entries key off "og_<id>" so a modifier group and an option
  // group that happen to share a numeric id can both be attached.
  const toggleNestedGroup = (group, isOptionGroup = false) => {
    const key = isOptionGroup ? `og_${group._id}` : String(group._id)
    setSelectedModifiers(prev => prev.map(m => {
      if (m.key !== nestedTargetKey) return m
      const current = m.nestedGroups || []
      const has = current.some(g => String(g._id) === key)
      const entry = isOptionGroup
        ? { _id: key, name: group.name, isOptionGroup: true, optionGroupId: String(group._id) }
        : { _id: key, name: group.name }
      return {
        ...m,
        nestedGroups: has
          ? current.filter(g => String(g._id) !== key)
          : [...current, entry]
      }
    }))
  }
  const removeNestedGroup = (key, groupId) => {
    setSelectedModifiers(prev => prev.map(m => (
      m.key === key
        ? { ...m, nestedGroups: (m.nestedGroups || []).filter(g => String(g._id) !== String(groupId)) }
        : m
    )))
  }

  // Every group except the one being edited — attaching a group to itself is the
  // one cycle we can rule out here. Deeper loops are caught server-side on save.
  const nestedGroupOptions = (groupsData?.advancedModifierGroups || [])
    .filter(g => !editingGroup || String(g._id) !== String(editingGroup._id))
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))

  // Option groups are free choice lists (spice level, temperature) — no prices
  // and nothing nests below them, so any of them can be a follow-up.
  const nestedOptionGroupOptions = (optionGroupsData?.advancedOptionGroups || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))

  const nestedTarget = selectedModifiers.find(m => m.key === nestedTargetKey)

  const handleFixedPriceChange = (key, price) => {
    setSelectedModifiers(selectedModifiers.map(m =>
      m.key === key ? { ...m, fixedPrice: price } : m
    ))
  }

  const handleVariantPriceChange = (key, variantName, price) => {
    setSelectedModifiers(selectedModifiers.map(m =>
      m.key === key
        ? { ...m, variantPrices: { ...m.variantPrices, [variantName]: price } }
        : m
    ))
  }

  // Bulk-fill: copy the first modifier's price for a variant column onto every
  // selected modifier in that same column. Saves setting each row by hand when
  // most toppings share a price (e.g. all Large = $3).
  const handleFillVariantColumn = (variantName) => {
    if (selectedModifiers.length === 0) return
    const value = selectedModifiers[0].variantPrices?.[variantName] ?? 0
    setSelectedModifiers(selectedModifiers.map(m => ({
      ...m,
      variantPrices: { ...m.variantPrices, [variantName]: value },
    })))
  }

  // Bulk-fill the single fixed-price column with the first modifier's price.
  const handleFillFixedColumn = () => {
    if (selectedModifiers.length === 0) return
    const value = selectedModifiers[0].fixedPrice ?? 0
    setSelectedModifiers(selectedModifiers.map(m => ({ ...m, fixedPrice: value })))
  }

  const handleAddVariantColumn = () => {
    const trimmed = newVariantName.trim()
    if (!trimmed || variantColumns.includes(trimmed)) return
    const updatedColumns = [...variantColumns, trimmed]
    setVariantColumns(updatedColumns)
    setNewVariantName('')
    setSelectedModifiers(selectedModifiers.map(m => ({
      ...m,
      variantPrices: { ...m.variantPrices, [trimmed]: 0 }
    })))
  }

  const handleRemoveVariantColumn = (colName) => {
    if (variantColumns.length <= 1) return
    setVariantColumns(variantColumns.filter(c => c !== colName))
    setSelectedModifiers(selectedModifiers.map(m => {
      const updated = { ...m.variantPrices }
      delete updated[colName]
      return { ...m, variantPrices: updated }
    }))
  }

  const handlePizzaModeToggle = (checked) => {
    setIsPizzaMode(checked)
    // Follow-up groups aren't offered in pizza mode, so don't leave their picker
    // open over a form that no longer shows the button.
    if (checked) closeNestedMenu()
    if (checked) {
      const defaultVariantPrices = {}
      variantColumns.forEach(col => {
        defaultVariantPrices[col] = 0
      })
      setSelectedModifiers(selectedModifiers.map(m => ({
        ...m,
        priceOverrideMode: 'VARIANT_BASED',
        variantPrices: m.variantPrices && Object.keys(m.variantPrices).length > 0
          ? m.variantPrices
          : defaultVariantPrices
      })))
    } else {
      setSelectedModifiers(selectedModifiers.map(m => ({
        ...m,
        priceOverrideMode: 'FIXED'
      })))
    }
  }

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })

  // Filter available modifiers by search
  const availableModifiers = (modifiersData?.advancedModifiers?.filter(mod =>
    mod.name.toLowerCase().includes(modifierSearch.toLowerCase())
  ) || []).slice().sort(byName)

  // Filter available items (item-as-modifier candidates) by search
  const availableItems = (itemsData?.advancedRestaurantItems?.filter(it =>
    it.name.toLowerCase().includes(modifierSearch.toLowerCase())
  ) || []).slice().sort(byName)

  const existingGroups = (groupsData?.advancedModifierGroups || []).slice().sort(byName)

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError(t('Group name is required'))
      return
    }

    // Auto-derive selectionType
    const derivedSelectionType = (isRequired && parseInt(maxSelections) === 1) ? 'SINGLE' : 'MULTIPLE'

    // displayOrder = position in the list, so the dragged order is exactly
    // what customers see when customizing an item.
    const modifiersInput = selectedModifiers.map((m, idx) => ({
      memberType: m.memberType || 'MODIFIER',
      modifierId: m.memberType === 'ITEM' ? null : m.modifierId,
      itemId: m.memberType === 'ITEM' ? m.itemId : null,
      priceOverrideMode: isPizzaMode ? 'VARIANT_BASED' : (parseFloat(m.fixedPrice) === 0 ? 'FREE' : 'FIXED'),
      fixedPrice: isPizzaMode ? null : (parseFloat(m.fixedPrice) || 0),
      variantPrices: isPizzaMode ? m.variantPrices : null,
      displayOrder: idx,
      isDefault: m.isDefault,
      // Item members already nest through the item's own groups, so only plain
      // modifiers carry these.
      nestedModifierGroupIds: m.memberType === 'ITEM'
        ? []
        : (m.nestedGroups || []).filter(g => !g.isOptionGroup).map(g => g._id),
      nestedOptionGroupIds: m.memberType === 'ITEM'
        ? []
        : (m.nestedGroups || []).filter(g => g.isOptionGroup).map(g => g.optionGroupId)
    }))

    const input = {
      name: name.trim(),
      description: description.trim(),
      selectionType: derivedSelectionType,
      minSelections: parseInt(minSelections) || 0,
      maxSelections: parseInt(maxSelections) || null,
      exactSelections: null,
      isRequired,
      groupRequirement,
      additionalCost: parseFloat(additionalCost) || 0,
      allowDuplicates,
      maxDuplicates: allowDuplicates ? (parseInt(maxDuplicates) || null) : null,
      duplicatePricingMode: allowDuplicates ? duplicatePricingMode : null,
      // INCREMENTAL stores a per-unit tier rule relative to each modifier's
      // base price: { "1": { mode, extra }, ... } up to maxDuplicates.
      //   SAME → base, FREE → $0, PLUS → base + extra, MINUS → base - extra
      duplicatePrices:
        allowDuplicates && duplicatePricingMode === 'INCREMENTAL'
          ? (() => {
              const max = parseInt(maxDuplicates) || 1
              const map = {}
              for (let i = 1; i <= max; i++) {
                const tier = duplicatePrices[String(i)] || { mode: 'SAME' }
                map[String(i)] =
                  tier.mode === 'PLUS' || tier.mode === 'MINUS'
                    ? { mode: tier.mode, extra: parseFloat(tier.extra) || 0 }
                    : { mode: tier.mode || 'SAME' }
              }
              return map
            })()
          : null,
      isPizzaMode,
      displayOrder: 0,
      isTemplate: false,
      modifiers: modifiersInput
    }

    if (editingGroup) {
      updateGroup({ variables: { id: editingGroup._id, input } })
    } else {
      createGroup({ variables: { restaurantId, input } })
    }
  }

  return (
    <Box>
      {/* ===== CREATED GROUPS LIST (hidden when used in dialog) ===== */}
      {!initialGroup && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ ...fieldLabelSx, mb: 1 }}>
              {t('Created Groups')} ({existingGroups.length})
            </Typography>

            {groupsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                <CircularProgress size={18} />
              </Box>
            ) : existingGroups.length > 0 ? (
              <Box sx={{
                maxHeight: 180,
                overflowY: 'auto',
                border: `1px solid ${mw.color.border}`,
                borderRadius: mw.radius.panel
              }}>
                {existingGroups.map(group => (
                  <Box
                    key={group._id}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: `1px solid ${mw.color.divider}`,
                      bgcolor: editingGroup?._id === group._id ? mw.color.accentSoft : 'transparent',
                      '&:hover': { bgcolor: editingGroup?._id === group._id ? mw.color.accentSoft : mw.color.pageBg }
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textSecondary }} noWrap>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                        {group.modifiers?.length || 0} {t('modifiers')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditGroup(group)}
                        sx={iconBtnSx}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteGroup(group._id)}
                        color="error"
                        sx={iconBtnDangerSx}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                {t('No groups created yet')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* Edit mode header */}
      {editingGroup && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          py: 1,
          px: 1.5,
          bgcolor: mw.color.accentSoft,
          border: `1px solid ${mw.color.accentBorder}`,
          borderRadius: mw.radius.sm
        }}>
          <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.accentDark }}>
            {t('Editing:')} {editingGroup.name}
          </Typography>
          <Button
            size="small"
            onClick={resetForm}
            sx={{ textTransform: 'none', fontWeight: 600, color: mw.color.textSecondary, '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' } }}
          >
            {t('Cancel')}
          </Button>
        </Box>
      )}

      {/* ===== GROUP FORM ===== */}
      <Box component="form" onSubmit={handleSubmit}>
        {/* Floating feedback — fixed to the screen so it's visible no matter
            how far down the form the user has scrolled. */}
        <Snackbar open={!!error} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error" sx={{ boxShadow: 3 }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" sx={{ boxShadow: 3 }}>{success}</Alert>
        </Snackbar>

        {/* Section 1: Group Name */}
        <TextField
          fullWidth
          label={t('Group Name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
          size="small"
          placeholder={t('e.g., Pizza Toppings, Spice Level')}
          InputLabelProps={{ sx: { color: mw.color.textMuted } }}
          FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
        />

        {/* Section 2: Min/Max in two-column Grid */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label={t('Min Choice')}
              value={minSelections}
              onChange={(e) => setMinSelections(e.target.value)}
              size="small"
              inputProps={{ min: 0 }}
              helperText={t('0 = optional group. 1+ = customer must pick at least this many.')}
              InputLabelProps={{ sx: { color: mw.color.textMuted } }}
              FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label={t('Max Choice')}
              value={maxSelections}
              onChange={(e) => setMaxSelections(e.target.value)}
              size="small"
              inputProps={{ min: 1 }}
              helperText={t('Max total selections a customer can pick (repeats count — e.g. the same option ×2 uses 2)')}
              InputLabelProps={{ sx: { color: mw.color.textMuted } }}
              FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
            />
          </Grid>
        </Grid>

        {/* Section 2a: Is the GROUP itself optional or required (separate from
            the modifiers-inside rule below). Optional + a cost = opt-in upsell. */}
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel sx={{ color: mw.color.textMuted }}>{t('Is this group optional or required?')}</InputLabel>
          <Select
            value={groupRequirement}
            label={t('Is this group optional or required?')}
            onChange={(e) => setGroupRequirement(e.target.value)}
          >
            <MenuItem value="REQUIRED">
              <Box>
                <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>{t('Required')}</Typography>
                <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                  {t('Customer must engage with this group')}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value="OPTIONAL">
              <Box>
                <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>{t('Optional')}</Typography>
                <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                  {t('Customer can skip it. With a cost, shown as an opt-in upgrade.')}
                </Typography>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* Section 2b: Flat group cost (optional) */}
        <TextField
          fullWidth
          type="number"
          label={t('Cost')}
          value={additionalCost}
          onChange={(e) => setAdditionalCost(e.target.value)}
          margin="normal"
          size="small"
          placeholder="0.00"
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  alignSelf: 'stretch', height: 'auto', maxHeight: 'none', m: 0, mr: 1.5, px: 1.5,
                  bgcolor: mw.color.subtleBg, borderRight: `1px solid ${mw.color.border}`,
                  borderRadius: `${mw.radius.sm} 0 0 ${mw.radius.sm}`
                }}
              >
                <Typography variant="caption" sx={{ color: mw.color.textSecondary, fontWeight: 700, fontSize: 14.5 }}>$</Typography>
              </InputAdornment>
            )
          }}
          sx={{ '& .MuiOutlinedInput-root': { pl: 0, fontVariantNumeric: 'tabular-nums' } }}
          inputProps={{ min: 0, step: 0.01 }}
          helperText={t('Optional flat charge added once when a customer picks from this group (e.g. make it a Plate +$3). Leave blank for no extra cost.')}
          InputLabelProps={{ sx: { color: mw.color.textMuted } }}
          FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
        />

        {/* Sections 3-5 sit side by side as three bordered option cards; an
            opened card (Multi Selecting / Pizza) spans the full row. */}
        <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, alignItems: 'stretch' }}>
        {/* Section 3: Multi Selecting (Allow Duplicates) */}
        <Box sx={{
          ...optionCardSx(allowDuplicates),
          gridColumn: allowDuplicates ? '1 / -1' : 'auto'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowDuplicates}
                onChange={(e) => setAllowDuplicates(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
                {t('Multi Selecting')}
              </Typography>
            }
          />
          <Typography variant="caption" sx={{ color: mw.color.textMuted, pl: 4, display: 'block', mt: -0.5 }}>
            {t('Lets customers pick the same option more than once (e.g. Extra Cheese ×3)')}
          </Typography>

          {allowDuplicates && (
            <Box sx={{ mt: 1.5, pl: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('How many choices?')}
                value={maxDuplicates}
                onChange={(e) => setMaxDuplicates(e.target.value)}
                size="small"
                inputProps={{ min: 2, max: 10 }}
                helperText={t('Max times the same option can be selected')}
                InputLabelProps={{ sx: { color: mw.color.textMuted } }}
                FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
              />

              <FormControl fullWidth margin="normal" size="small">
                <InputLabel sx={{ color: mw.color.textMuted }}>{t('Pricing')}</InputLabel>
                <Select
                  value={duplicatePricingMode}
                  onChange={(e) => setDuplicatePricingMode(e.target.value)}
                  label={t('Pricing')}
                >
                  <MenuItem value="SAME">
                    <Box>
                      <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>{t('Same')}</Typography>
                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                        {t('e.g., $1.00 every time')}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="FIRST_FREE_THEN_CHARGED">
                    <Box>
                      <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>{t('1st Free')}</Typography>
                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                        {t('e.g., 1st = $0, 2nd+ = $1.00')}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="INCREMENTAL">
                    <Box>
                      <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>{t('Incremental')}</Typography>
                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                        {t('Per unit: Same, Free, or Base + extra (e.g. 2nd = base + $0.50)')}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Incremental tier rules: per unit, choose Same / Free / Base + extra.
                  Prices are relative to each modifier's base price (set in the
                  table below), so this works across modifiers and pizza sizes. */}
              {duplicatePricingMode === 'INCREMENTAL' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
                    {t('Set how each successive selection is priced relative to the base price. Customers are charged the sum of the chosen units.')}
                  </Typography>
                  <Grid container spacing={1}>
                    {Array.from({ length: parseInt(maxDuplicates) || 1 }, (_, i) => i + 1).map((unit) => {
                      const tier = duplicatePrices[String(unit)] || { mode: 'SAME' }
                      const ord = `${unit}${unit === 1 ? 'st' : unit === 2 ? 'nd' : unit === 3 ? 'rd' : 'th'}`
                      const setTier = (patch) =>
                        setDuplicatePrices((prev) => ({
                          ...prev,
                          [String(unit)]: { ...(prev[String(unit)] || { mode: 'SAME' }), ...patch }
                        }))
                      return (
                        <Grid item xs={12} sm={6} key={unit}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: mw.color.textSecondary, minWidth: 28 }}>{ord}</Typography>
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                              <Select
                                value={tier.mode || 'SAME'}
                                onChange={(e) => setTier({ mode: e.target.value })}
                              >
                                <MenuItem value="SAME">{t('Same')}</MenuItem>
                                <MenuItem value="FREE">{t('Free')}</MenuItem>
                                <MenuItem value="PLUS">{t('Base + extra')}</MenuItem>
                                <MenuItem value="MINUS">{t('Base - amount')}</MenuItem>
                              </Select>
                            </FormControl>
                            {(tier.mode === 'PLUS' || tier.mode === 'MINUS') && (
                              <TextField
                                size="small"
                                type="number"
                                placeholder="0.50"
                                value={tier.extra ?? ''}
                                onChange={(e) => setTier({ extra: e.target.value })}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                                        {tier.mode === 'MINUS' ? '-$' : '+$'}
                                      </Typography>
                                    </InputAdornment>
                                  )
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 110 }}
                              />
                            )}
                          </Box>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Section 4: Required Choices */}
        <Box sx={optionCardSx(isRequired)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>{t('Required Choices')}</Typography>}
          />
          <Typography variant="caption" sx={{ color: mw.color.textMuted, pl: 4, display: 'block', mt: -0.5 }}>
            {t('Customer must select from this group before adding item to cart')}
          </Typography>
        </Box>

        {/* Section 5: Is this for Pizza? */}
        <Box sx={{
          ...optionCardSx(isPizzaMode),
          gridColumn: isPizzaMode ? '1 / -1' : 'auto'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isPizzaMode}
                onChange={(e) => handlePizzaModeToggle(e.target.checked)}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
                {t('Is this for Pizza?')}
                <Typography component="span" variant="caption" sx={{ color: mw.color.textMuted, ml: 1 }}>
                  {t('(variant-based pricing)')}
                </Typography>
              </Typography>
            }
          />

          {isPizzaMode && (
            <Box sx={{ mt: 1.5, pl: 4 }}>
              <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
                {t('Size columns:')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {variantColumns.map(col => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    onDelete={variantColumns.length > 1 ? () => handleRemoveVariantColumn(col) : undefined}
                    sx={{ ...chipSx.category, bgcolor: mw.color.cardBg, border: `1px solid ${mw.color.border}`, '& .MuiChip-deleteIcon': { fontSize: 16, color: mw.color.textFaint, '&:hover': { color: mw.color.danger } } }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('Add size...')}
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddVariantColumn()
                    }
                  }}
                  sx={{ flex: 1 }}
                  InputLabelProps={{ sx: { color: mw.color.textMuted } }}
                />
                <IconButton
                  size="small"
                  onClick={handleAddVariantColumn}
                  disabled={!newVariantName.trim()}
                  color="primary"
                  sx={{ ...iconBtnSx, color: mw.color.accent, border: `1px solid ${mw.color.accentBorder}`, bgcolor: mw.color.cardBg, '&:hover': { bgcolor: mw.color.accentSoft } }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
        </Box>

        {/* Section 6: Add Members — plain modifiers OR menu items (item-as-modifier) */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ ...fieldLabelSx, mb: 1 }}>
            {t('Add Modifier')}
          </Typography>

          {/* Picker mode toggle: plain modifiers vs full menu items */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant={pickerMode === 'MODIFIER' ? 'contained' : 'outlined'}
              onClick={() => setPickerMode('MODIFIER')}
              sx={{ ...(pickerMode === 'MODIFIER' ? pillBtnPrimarySx : pillBtnSx), boxShadow: 'none', flex: 1 }}
            >
              {t('Modifiers')}
            </Button>
            <Button
              size="small"
              variant={pickerMode === 'ITEM' ? 'contained' : 'outlined'}
              onClick={() => setPickerMode('ITEM')}
              sx={{ ...(pickerMode === 'ITEM' ? pillBtnPrimarySx : pillBtnSx), boxShadow: 'none', flex: 1 }}
            >
              {t('Menu Items')}
            </Button>
          </Box>
          {pickerMode === 'ITEM' && (
            <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
              {t('Items keep their own modifier groups — the customer customizes them when picking this add-on. Set the add-on price below.')}
            </Typography>
          )}

          {/* Search bar */}
          <TextField
            fullWidth
            size="small"
            placeholder={pickerMode === 'ITEM' ? t('Search items...') : t('Search modifiers...')}
            value={modifierSearch}
            onChange={(e) => setModifierSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: mw.color.textMuted }} />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ sx: { color: mw.color.textMuted } }}
            sx={{ mb: 1 }}
          />

          {/* Available list (modifiers OR items, by pickerMode) */}
          <Box sx={{
            maxHeight: 150,
            overflowY: 'auto',
            border: `1px solid ${mw.color.border}`,
            borderRadius: mw.radius.panel,
            mb: 2
          }}>
            {(pickerMode === 'ITEM' ? itemsLoading : modifiersLoading) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (pickerMode === 'ITEM' ? availableItems : availableModifiers).length > 0 ? (
              (pickerMode === 'ITEM' ? availableItems : availableModifiers).map((entity) => {
                const memberKey = pickerMode === 'ITEM' ? `item_${entity._id}` : `mod_${entity._id}`
                const isSelected = selectedModifiers.find(m => m.key === memberKey)
                return (
                  <Box
                    key={entity._id}
                    onClick={() => !isSelected && handleAddMember(entity, pickerMode)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: isSelected ? 'default' : 'pointer',
                      opacity: isSelected ? 0.5 : 1,
                      bgcolor: isSelected ? mw.color.subtleBg : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? mw.color.subtleBg : mw.color.accentSoft
                      },
                      borderBottom: `1px solid ${mw.color.divider}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: isSelected ? mw.color.textFaint : mw.color.textPrimary }}>
                      {entity.name}
                    </Typography>
                    {isSelected ? (
                      <Typography variant="caption" sx={{ color: mw.color.textFaint, fontWeight: 600 }}>{t('Added')}</Typography>
                    ) : (
                      <AddIcon fontSize="small" sx={{ color: mw.color.textMuted }} />
                    )}
                  </Box>
                )
              })
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block' }}>
                  {pickerMode === 'ITEM'
                    ? (modifierSearch ? t('No items match your search') : t('No menu items yet. Create items first.'))
                    : (modifierSearch ? t('No modifiers match your search') : t('No modifiers yet. Create modifiers in Column 1 first.'))}
                </Typography>
                {/* Inline-create the searched modifier (modifier picker only). */}
                {pickerMode === 'MODIFIER' && modifierSearch.trim() && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={creatingModifier ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
                    disabled={creatingModifier}
                    onClick={handleCreateAndAddModifier}
                    sx={{ ...pillBtnSx, mt: 1 }}
                  >
                    {t('Create "{{name}}" & add', { name: modifierSearch.trim() })}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Selected modifiers table with inline pricing */}
          {selectedModifiers.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" fontWeight="bold" sx={fieldLabelSx}>
                  {t('Selected')} ({selectedModifiers.length})
                </Typography>
                {selectedModifiers.length > 1 && (
                  <Button size="small" startIcon={<SwapVertIcon />} onClick={openMembersReorder} sx={{ textTransform: 'none', fontWeight: 600, color: mw.color.accent, py: 0, minHeight: 28 }}>
                    {t('Reorder')}
                  </Button>
                )}
              </Box>
              <TableContainer sx={{ border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: mw.color.headBg }}>
                      <TableCell sx={{ color: mw.color.textMuted, fontWeight: 'bold', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', py: 1 }}>
                        {t('Modifier')}
                      </TableCell>
                      {isPizzaMode ? (
                        variantColumns.map(col => (
                          <TableCell
                            key={col}
                            align="center"
                            sx={{ color: mw.color.textMuted, fontWeight: 'bold', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', py: 1, minWidth: 70 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                              {col}
                              {selectedModifiers.length > 1 && (
                                <Tooltip title={t('Fill this column with the top row\'s price')}>
                                  <IconButton size="small" onClick={() => handleFillVariantColumn(col)} sx={{ p: 0.25, color: mw.color.accent }}>
                                    <SouthIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        ))
                      ) : (
                        <TableCell
                          align="center"
                          sx={{ color: mw.color.textMuted, fontWeight: 'bold', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', py: 1, minWidth: 80 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                            {t('Price')}
                            {selectedModifiers.length > 1 && (
                              <Tooltip title={t('Fill this column with the top row\'s price')}>
                                <IconButton size="small" onClick={handleFillFixedColumn} sx={{ p: 0.25, color: mw.color.accent }}>
                                  <SouthIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ width: 40, py: 0.75 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedModifiers.map((modifier) => {
                      const isItem = modifier.memberType === 'ITEM'
                      const itemGroups = modifier.itemModifierGroups || []
                      const canExpand = isItem && itemGroups.length > 0
                      const isExpanded = expandedKeys.has(modifier.key)
                      const previewColSpan = (isPizzaMode ? variantColumns.length : 1) + 2
                      return (
                      <React.Fragment key={modifier.key}>
                      <TableRow>
                        <TableCell sx={{ color: mw.color.textPrimary, fontSize: 13.5, fontWeight: 600, py: 0.75 }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: canExpand ? 'pointer' : 'default' }}
                            onClick={() => canExpand && toggleExpanded(modifier.key)}
                          >
                            {canExpand && (
                              <Typography component="span" sx={{ fontSize: '0.7rem', color: mw.color.emerald, width: 10, lineHeight: 1 }}>
                                {isExpanded ? '▾' : '▸'}
                              </Typography>
                            )}
                            {modifier.modifierName}
                            {isItem && (
                              <Chip
                                label={t('Item')}
                                size="small"
                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, borderRadius: mw.radius.badge, bgcolor: mw.color.emeraldSoft, color: mw.color.emerald }}
                              />
                            )}
                            {canExpand && (
                              <Typography component="span" variant="caption" sx={{ color: mw.color.textMuted }}>
                                ({itemGroups.length} {itemGroups.length === 1 ? t('group') : t('groups')})
                              </Typography>
                            )}
                          </Box>
                          {/* Groups revealed when this modifier is picked. Item
                              members nest through their own groups instead, and
                              pizza mode prices every member per size — a
                              follow-up group has no size to price against, so
                              it can't be added there. Any group attached before
                              pizza mode was turned on stays visible so it can be
                              removed rather than silently dropped. */}
                          {!isItem && (isPizzaMode ? (modifier.nestedGroups || []).length > 0 : true) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {(modifier.nestedGroups || []).map(g => (
                                <Chip
                                  key={g._id}
                                  label={g.name}
                                  size="small"
                                  onDelete={() => removeNestedGroup(modifier.key, g._id)}
                                  sx={{
                                    height: 22,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    borderRadius: mw.radius.pill,
                                    // Option groups wear the workspace's amber,
                                    // modifier groups green, so the two kinds
                                    // are told apart at a glance.
                                    ...(g.isOptionGroup
                                      ? { bgcolor: mw.color.amberSoft, color: mw.color.amber }
                                      : { bgcolor: mw.color.emeraldSoft, color: mw.color.emerald }),
                                    '& .MuiChip-deleteIcon': { fontSize: 15, color: 'currentColor', opacity: 0.45, '&:hover': { opacity: 1, color: mw.color.danger } }
                                  }}
                                />
                              ))}
                              {!isPizzaMode && (
                                <Button
                                  size="small"
                                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                  onClick={(e) => openNestedMenu(e, modifier.key)}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.65rem',
                                    py: 0,
                                    px: 1,
                                    minWidth: 0,
                                    minHeight: 22,
                                    borderRadius: mw.radius.pill,
                                    border: `1px dashed ${mw.color.borderStrong}`,
                                    color: mw.color.textMuted,
                                    '&:hover': { borderColor: mw.color.accent, color: mw.color.accent, bgcolor: mw.color.accentSoft }
                                  }}
                                >
                                  {(modifier.nestedGroups || []).length > 0 ? t('Add group') : t('Add follow-up group')}
                                </Button>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        {isPizzaMode ? (
                          variantColumns.map(col => (
                            <TableCell key={col} align="center" sx={{ py: 0.5, px: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={modifier.variantPrices?.[col] ?? 0}
                                onChange={(e) => handleVariantPriceChange(modifier.key, col, e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="caption" sx={{ color: mw.color.textMuted }}>$</Typography>
                                    </InputAdornment>
                                  )
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{
                                  width: '100%',
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5, px: 0.5 }
                                }}
                              />
                            </TableCell>
                          ))
                        ) : (
                          <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={modifier.fixedPrice}
                              onChange={(e) => handleFixedPriceChange(modifier.key, e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography variant="caption" sx={{ color: mw.color.textMuted }}>$</Typography>
                                  </InputAdornment>
                                )
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{
                                width: 100,
                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5, px: 0.5 }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMember(modifier.key)}
                            color="error"
                            sx={{ ...iconBtnDangerSx, width: 32, height: 32 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {canExpand && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={previewColSpan} sx={{ bgcolor: mw.color.headBg, py: 1.25, px: 2, borderBottom: `1px solid ${mw.color.divider}` }}>
                            <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 0.5 }}>
                              {t("This item's own groups (customer customizes these):")}
                            </Typography>
                            {itemGroups.map((g) => (
                              <Box key={g._id} sx={{ mb: 0.75 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ color: mw.color.textSecondary, display: 'block' }}>
                                  {g.name}{g.isRequired ? ` (${t('Required')})` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, pl: 1 }}>
                                  {(g.modifiers || []).length > 0 ? (
                                    (g.modifiers || []).map((gm) => (
                                      <Chip
                                        key={gm._id}
                                        label={`${gm.modifier?.name || gm.item?.name || '?'}${parseFloat(gm.fixedPrice) > 0 ? ` $${parseFloat(gm.fixedPrice).toFixed(2)}` : ''}`}
                                        size="small"
                                        sx={{ fontSize: 11.5, height: 22, fontWeight: 600, borderRadius: mw.radius.pill, bgcolor: '#fff', color: mw.color.textSecondary, border: `1px solid ${mw.color.border}` }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="caption" sx={{ color: mw.color.textFaint }}>{t('(no options)')}</Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {selectedModifiers.length === 0 && (
            <Alert severity="info" sx={{ fontSize: 13, bgcolor: mw.color.headBg, color: mw.color.textSecondary, border: `1px dashed ${mw.color.border}`, '& .MuiAlert-icon': { color: mw.color.textFaint } }}>
              {t('Click modifiers above to add them to this group')}
            </Alert>
          )}

          {/* Picker for the groups a modifier reveals when it is chosen. Stays
              open across ticks so several can be attached in one pass. */}
          <MuiMenu
            anchorEl={nestedAnchor}
            open={Boolean(nestedAnchor)}
            onClose={closeNestedMenu}
            PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold', display: 'block' }}>
                {nestedTarget
                  ? t('Ask these after "{{m}}" is picked', { m: nestedTarget.modifierName })
                  : t('Follow-up groups')}
              </Typography>
            </Box>
            <Divider />
            {nestedGroupOptions.length === 0 && nestedOptionGroupOptions.length === 0 ? (
              <MenuItem disabled>{t('No other groups yet')}</MenuItem>
            ) : (
              <>
                {nestedGroupOptions.length > 0 && (
                  <Box sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" sx={sectionLabelSx.mg}>
                      {t('Modifier Groups')}
                    </Typography>
                  </Box>
                )}
                {nestedGroupOptions.map((g) => {
                  const checked = (nestedTarget?.nestedGroups || [])
                    .some(n => String(n._id) === String(g._id))
                  return (
                    <MenuItem key={`mg_${g._id}`} onClick={() => toggleNestedGroup(g)} dense>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => { e.stopPropagation(); toggleNestedGroup(g) }}
                      />
                      <ListItemText primary={g.name} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                  )
                })}
                {nestedOptionGroupOptions.length > 0 && (
                  <Box sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" sx={sectionLabelSx.og}>
                      {t('Option Groups')}
                    </Typography>
                  </Box>
                )}
                {nestedOptionGroupOptions.map((g) => {
                  const checked = (nestedTarget?.nestedGroups || [])
                    .some(n => String(n._id) === `og_${g._id}`)
                  return (
                    <MenuItem key={`og_${g._id}`} onClick={() => toggleNestedGroup(g, true)} dense>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => { e.stopPropagation(); toggleNestedGroup(g, true) }}
                      />
                      <ListItemText
                        primary={g.name}
                        secondary={(g.choices || []).join(', ')}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </MenuItem>
                  )
                })}
              </>
            )}
            <Divider />
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" onClick={closeNestedMenu} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                {t('Done')}
              </Button>
            </Box>
          </MuiMenu>
        </Box>

        {/* Section 7: Description (moved to bottom) */}
        <TextField
          fullWidth
          label={t('Description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          size="small"
          multiline
          rows={2}
          placeholder={t('Customer-facing description')}
          InputLabelProps={{ sx: { color: mw.color.textMuted } }}
          FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
          sx={{ mt: 2 }}
        />

        {/* Section 8: Submit */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {editingGroup && (
            <Button
              variant="outlined"
              onClick={resetForm}
              sx={{ ...pillBtnSx, flex: 1 }}
            >
              {t('New Group')}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            color={editingGroup ? 'info' : 'primary'}
            fullWidth={!editingGroup}
            disabled={loading}
            sx={{ ...pillBtnPrimarySx, flex: editingGroup ? 1 : undefined }}
          >
            {loading ? <CircularProgress size={24} /> : (editingGroup ? t('Update Group') : t('Create Group'))}
          </Button>
        </Box>
      </Box>

      {/* Reorder members dialog — drag into the order customers see them */}
      <Dialog
        open={membersReorderOpen}
        onClose={() => setMembersReorderOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {t('Reorder')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>
            {t('Drag into the order customers should see them. Click "{{btn}}" below, then save the group.', { btn: t('Apply Order') })}
          </Typography>
          <DragDropContext onDragEnd={handleMembersDragEnd}>
            <Droppable droppableId="group-members-reorder">
              {(dropProvided) => (
                <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {memberOrderList.map((m, index) => (
                    <Draggable key={m.key} draggableId={`member-${m.key}`} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 1, mb: 0.75,
                            borderRadius: '10px',
                            border: `1px solid ${dragSnapshot.isDragging ? mw.color.accentBorder : mw.color.border}`,
                            bgcolor: dragSnapshot.isDragging ? mw.color.accentSoft : '#fff',
                            boxShadow: dragSnapshot.isDragging ? mw.shadow.pop : 'none',
                            cursor: 'grab'
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" sx={{ color: mw.color.textFaint }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: mw.color.textSecondary }}>
                            {m.name}
                          </Typography>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersReorderOpen(false)} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={applyMembersOrder} sx={pillBtnPrimarySx}>
            {t('Apply Order')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Column3GroupBuilder
